import SkeletonBox from '@src/components/common/SkeletonBox';
import styled from 'styled-components';

// ── Skeleton Story Card ──
// Matches HomeStoryCard layout: card shell, 16:9 thumbnail, info section with text lines + stats

const SkeletonCardShell = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  background-color: var(--surface);
  border-radius: 8px;
  overflow: hidden;
`;

const SkeletonThumbnail = styled(SkeletonBox)`
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 */
  height: auto;
  border-radius: 0;
`;

const SkeletonInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
  gap: 8px;
`;

const SkeletonStatsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 4px;
`;

export function SkeletonStoryCard() {
  return (
    <SkeletonCardShell>
      <SkeletonThumbnail />
      <SkeletonInfoSection>
        <SkeletonBox $width="80%" $height="14px" />
        <SkeletonBox $width="60%" $height="11px" />
        <SkeletonBox $width="50%" $height="11px" />
        <SkeletonStatsRow>
          <SkeletonBox $width="50px" $height="11px" />
          <SkeletonBox $width="60px" $height="11px" />
        </SkeletonStatsRow>
      </SkeletonInfoSection>
    </SkeletonCardShell>
  );
}

// ── Skeleton Table Rows ──
// Matches RecentContent table: header + N placeholder rows

const Table = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--surface);
  border-radius: 8px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background-color: var(--background);
  border-bottom: 1px solid var(--popover);
`;

const HeaderCell = styled.div`
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: var(--text-muted);
  letter-spacing: 0.5px;
`;

const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--popover);

  &:last-child {
    border-bottom: none;
  }
`;

const RowCell = styled.div<{ $width?: string; $flex?: number }>`
  display: flex;
  align-items: center;
  width: ${props => props.$width ?? 'auto'};
  ${props => (props.$flex ? `flex: ${props.$flex};` : '')}
`;

const TitleLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

interface SkeletonTableRowsProps {
  count?: number;
}

export function SkeletonTableRows({ count = 5 }: SkeletonTableRowsProps) {
  return (
    <Table>
      <TableHeader>
        <HeaderCell style={{ flex: 1, minWidth: 0 }}>Content Title</HeaderCell>
        <HeaderCell style={{ width: '60px' }}></HeaderCell>
        <HeaderCell style={{ width: '300px' }}>Story</HeaderCell>
        <HeaderCell style={{ width: '120px' }}>Updated By</HeaderCell>
        <HeaderCell style={{ width: '100px' }}>Type</HeaderCell>
        <HeaderCell style={{ width: '120px' }}>Created</HeaderCell>
      </TableHeader>
      {Array.from({ length: count }, (_, i) => (
        <SkeletonRow key={i}>
          <RowCell $width="60px">
            <SkeletonBox $width="48px" $height="32px" />
          </RowCell>
          <RowCell $flex={1} style={{ paddingRight: '16px', minWidth: 0 }}>
            <TitleLines>
              <SkeletonBox $width="70%" $height="13px" />
            </TitleLines>
          </RowCell>
          <RowCell $width="300px" style={{ paddingRight: '16px' }}>
            <SkeletonBox $width="60%" $height="12px" />
          </RowCell>
          <RowCell $width="120px">
            <SkeletonBox $width="80px" $height="12px" />
          </RowCell>
          <RowCell $width="100px">
            <SkeletonBox $width="52px" $height="22px" $radius="4px" />
          </RowCell>
          <RowCell $width="120px">
            <SkeletonBox $width="80px" $height="12px" />
          </RowCell>
        </SkeletonRow>
      ))}
    </Table>
  );
}
